import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Job } from '@/constants/jobs'

import Link from 'next/link'
import { ArrowLeft, Mail, Phone } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SplitWords } from '@/components/generals/split-words'
import { SectionLabel } from '@/components/generals/section-label'
import { JOBS, JOB_CONTACT, getJob } from '@/constants/jobs'

interface JobPageProps {
    job: Job
}

const List: FC<{ items: string[] }> = ({ items }): ReactNode => (
    <ul className='mt-6 border-t border-tone-line'>
        {items.map((item, i) => (
            <li key={item} className='flex gap-5 border-b border-tone-line py-4' data-reveal='rise' style={{ '--d': i } as CSSProperties}>
                <span className='t-label tabular pt-1 text-tone-ink'>{String(i + 1).padStart(2, '0')}</span>
                <span className='pretty'>{item}</span>
            </li>
        ))}
    </ul>
)

const JobPage: FC<JobPageProps> = ({ job }): ReactNode => {
    const subject = encodeURIComponent(`Søknad: ${job.title}`)

    return (
        <Layout header='dark'>
            <Seo title={job.title} description={`${job.title} i Svenn. ${job.teaser} ${job.place}, oppstart ${job.start.toLowerCase()}.`} />

            <section id='stilling' className='tone-night grain relative overflow-hidden' data-header='dark'>
                <div className='grid-lines' />

                <div className='wrap relative pt-[calc(var(--header-h)+4rem)] pb-16 md:pb-24'>
                    <Link href='/ledige-stillinger' className='load-fade t-label inline-flex items-center gap-2 text-tone-muted hover:text-tone-fg' style={{ '--load-delay': '0.05s' } as CSSProperties}>
                        <ArrowLeft className='size-4' aria-hidden='true' />
                        Alle stillinger
                    </Link>

                    <h1 className='t-display mt-10 max-w-[14ch] balance'>
                        <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                            <SplitWords text={job.title} />
                        </span>
                    </h1>

                    <dl className='load-fade mt-12 grid max-w-3xl grid-cols-2 gap-6 border-t border-tone-line pt-6 sm:grid-cols-3' style={{ '--load-delay': '0.6s' } as CSSProperties}>
                        <div>
                            <dt className='t-label text-tone-muted'>Arbeidssted</dt>
                            <dd className='mt-2 font-semibold'>{job.place}</dd>
                        </div>
                        <div>
                            <dt className='t-label text-tone-muted'>Oppstart</dt>
                            <dd className='mt-2 font-semibold'>{job.start}</dd>
                        </div>
                        <div>
                            <dt className='t-label text-tone-muted'>Kontakt</dt>
                            <dd className='mt-2 font-semibold'>{JOB_CONTACT.name}</dd>
                        </div>
                    </dl>
                </div>
            </section>

            <article className='tone-paper relative py-20 md:py-32' data-header='light'>
                <div className='wrap grid grid-cols-12 gap-x-6 gap-y-16'>
                    <div className='col-span-12 lg:col-span-7'>
                        {job.intro.map((paragraph, i) => (
                            <p key={paragraph} className={i === 0 ? 't-lead pretty' : 'mt-6 text-tone-muted t-body pretty'}>{paragraph}</p>
                        ))}

                        <section className='mt-16' aria-labelledby='om-rollen'>
                            <SectionLabel index='01'>Om rollen</SectionLabel>
                            <h2 id='om-rollen' className='t-h2 mt-5'>Dette blir hverdagen din</h2>
                            <p className='mt-5 text-tone-muted t-body pretty'>{job.roleIntro}</p>
                            <List items={job.responsibilities} />
                        </section>

                        <section className='mt-16' aria-labelledby='hvem'>
                            <SectionLabel index='02'>Hvem vi ser etter</SectionLabel>
                            <h2 id='hvem' className='t-h2 mt-5'>Du tar initiativ og liker folk</h2>
                            <List items={job.lookingFor} />
                            <p className='mt-6 inline-block bg-butter px-3 py-2 text-sm font-semibold'>Krav: {job.requirement}</p>
                        </section>

                        <section className='mt-16' aria-labelledby='vi-tilbyr'>
                            <SectionLabel index='03'>Hva vi tilbyr</SectionLabel>
                            <h2 id='vi-tilbyr' className='t-h2 mt-5'>Høyt tempo, lave skuldre</h2>
                            <List items={job.offer} />
                        </section>
                    </div>

                    <aside className='col-span-12 lg:col-span-4 lg:col-start-9'>
                        <div className='tone-night p-6 md:p-8 lg:sticky lg:top-28'>
                            <p className='t-label text-tone-muted'>Søk på stillingen</p>
                            <p className='t-h3 mt-4 balance'>Send noen ord om hvem du er, og hvorfor du passer hos Svenn.</p>

                            <div className='mt-8 flex flex-col gap-3'>
                                <a href={`mailto:${JOB_CONTACT.email}?subject=${subject}`} className='btn btn-lg w-full justify-between'>
                                    <span>Send søknad</span>
                                    <Mail className='size-4' aria-hidden='true' />
                                </a>
                                <a href={JOB_CONTACT.phoneHref} className='btn btn-ghost w-full justify-between'>
                                    <span className='tabular'>Ring {JOB_CONTACT.phone}</span>
                                    <Phone className='size-4' aria-hidden='true' />
                                </a>
                            </div>

                            <p className='mt-8 border-t border-tone-line pt-5 text-sm text-tone-muted'>
                                <span className='font-semibold text-tone-fg'>{JOB_CONTACT.name}</span>, {JOB_CONTACT.role}
                                <span className='block'>{JOB_CONTACT.email}</span>
                            </p>
                        </div>
                    </aside>
                </div>
            </article>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: JOBS.map(job => ({ params: { slug: job.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<JobPageProps> = async ({ params }) => {
    const job = getJob(String(params?.slug ?? ''))

    if (!job) return { notFound: true }

    return { props: { job } }
}

export default JobPage
